import { AfterViewInit, Component, ElementRef, OnDestroy } from '@angular/core';
import { CustomDetailComponent } from '@vendure/admin-ui/core';
import { Observable } from 'rxjs';
import { PROMOTION_CUSTOM_FIELDS_LABEL } from '../constants';

/**
 * Renames the "Custom Fields" card title on the promotion-detail page to
 * "Your Custom Promotion Settings" for better UX clarity.
 *
 * vdr-card renders its title as: <span class="title">{{ title }}</span>
 * inside a <div class="title-row"> inside a <div class="card-container">.
 *
 * Because vdr-card uses OnPush change detection, setting textContent after
 * Angular finishes the initial render cycle persists without being overwritten.
 * A MutationObserver handles the case where the card renders asynchronously
 * (e.g. *ngIf="customFields.length" resolves after our AfterViewInit).
 */
@Component({
    selector: 'promotion-custom-fields-label',
    template: '',
    standalone: true,
})
export class PromotionCustomFieldsLabelComponent implements CustomDetailComponent, AfterViewInit, OnDestroy {
    entity$!: Observable<any>;
    detailForm!: any;

    private observer: MutationObserver | null = null;

    constructor(private hostElement: ElementRef<HTMLElement>) {}

    ngAfterViewInit() {
        // Defer past the current render cycle so promotion-detail has finished
        // rendering all its children (including the *ngIf custom-fields card).
        setTimeout(() => {
            if (!this.applyTitleChange()) {
                // Card not yet in the DOM (e.g. async data not loaded) — watch for it.
                this.watchForCard();
            }
        }, 0);
    }

    ngOnDestroy() {
        this.observer?.disconnect();
    }

    private applyTitleChange(): boolean {
        const promotionDetail = this.hostElement.nativeElement.closest('vdr-promotion-detail');
        if (!promotionDetail) return false;

        const tabbedCustomFields = promotionDetail.querySelector('vdr-tabbed-custom-fields');
        if (!tabbedCustomFields) return false;

        const card = tabbedCustomFields.closest<HTMLElement>('vdr-card');
        if (!card) return false;

        const titleSpan = card.querySelector<HTMLElement>('span.title');
        if (!titleSpan) return false;

        titleSpan.textContent = PROMOTION_CUSTOM_FIELDS_LABEL;
        return true;
    }

    private watchForCard() {
        const promotionDetail = this.hostElement.nativeElement.closest('vdr-promotion-detail');
        if (!promotionDetail) return;

        this.observer = new MutationObserver(() => {
            if (this.applyTitleChange()) {
                this.observer?.disconnect();
                this.observer = null;
            }
        });

        this.observer.observe(promotionDetail, { childList: true, subtree: true });
    }
}
